import React, { createContext, useContext, useState } from 'react';
import type {
	ConversationSidebarActions,
	ConversationSidebarState,
	EntityAgentReference,
} from '../types/agent-references';

const ConversationSidebarContext = createContext<
	(ConversationSidebarState & ConversationSidebarActions) | undefined
>(undefined);

export const ConversationSidebarProvider: React.FC<{
	children: React.ReactNode;
}> = ({ children }) => {
	const [state, setState] = useState<ConversationSidebarState>({
		isOpen: false,
		entityType: null,
		entityId: null,
		references: null,
		selectedReferenceIndex: 0,
		explicitSelection: false,
	});

	const openConversationSidebar = (
		entityType: string,
		entityId: number,
		references: Array<EntityAgentReference>,
	) => {
		setState({
			isOpen: true,
			entityType,
			entityId,
			references,
			selectedReferenceIndex: 0,
			explicitSelection: false,
		});
	};

	const closeConversationSidebar = () => {
		setState({
			isOpen: false,
			entityType: null,
			entityId: null,
			references: null,
			selectedReferenceIndex: 0,
			explicitSelection: false,
		});
	};

	const selectReference = (index: number) => {
		setState((prev: ConversationSidebarState) => ({
			...prev,
			selectedReferenceIndex: index,
			explicitSelection: true,
		}));
	};

	return (
		<ConversationSidebarContext.Provider
			value={{
				...state,
				openConversationSidebar,
				closeConversationSidebar,
				selectReference,
			}}
		>
			{children}
		</ConversationSidebarContext.Provider>
	);
};

export const useConversationSidebar = () => {
	const context = useContext(ConversationSidebarContext);

	if (context === undefined) {
		throw new Error(
			'useConversationSidebar must be used within ConversationSidebarProvider',
		);
	}

	return context;
};
